/**
 * Benutzer sperren statt löschen (Migration Nr. 138, `supabase/benutzer_sperren.sql`).
 *
 * „Benutzer löschen" entfernte nur die `profiles`-Zeile — das Anmeldekonto
 * blieb, und ein Konto ohne Profil gilt in der Datenbank als intern. Die
 * Sperre setzt stattdessen `auth.users.banned_until` und beendet alle
 * Sitzungen; Profil und Nachweise bleiben, die Sperre ist umkehrbar.
 *
 * `auth.users` ist für den Client nicht lesbar: Status und Aktion laufen nur
 * über die beiden RPCs. Fehlt die Migration, sagt der Dienst das im Klartext,
 * statt einen Knopf anzubieten, der ins Leere läuft.
 */
import { supabase } from './supabaseClient';

export interface SperrStatus {
    gesperrtBis: string | null;
    letzteAnmeldung: string | null;
}

// Bewusst keine Diskriminanten-Union: ohne strictNullChecks engt TypeScript
// `if (!r.verfuegbar)` nicht ein. `grund` ist genau dann gesetzt, wenn
// `verfuegbar` falsch ist.
export interface SperrStatusErgebnis {
    verfuegbar: boolean;
    status: Map<string, SperrStatus>;
    grund: string | null;
}

export interface SperrErgebnis {
    ok: boolean;
    gesperrtBis: string | null;
    meldung: string | null;
}

export const MIGRATION_FEHLT = 'Sperren ist in dieser Datenbank noch nicht eingerichtet (Migration Nr. 138 offen).';

interface RpcFehler { code?: string; message?: string }

/** PGRST202 = Funktion im Schema-Cache unbekannt, 42883 = Funktion fehlt. */
const fehltFunktion = (e: RpcFehler | null | undefined): boolean =>
    !!e && (e.code === 'PGRST202' || e.code === '42883');

export const istGesperrt = (s: SperrStatus | undefined, jetztMs: number = Date.now()): boolean => {
    if (!s?.gesperrtBis) return false;
    const bis = Date.parse(s.gesperrtBis);
    return Number.isFinite(bis) && bis > jetztMs;
};

/** Die Meldungen der RPC in Sätze für die Benutzerverwaltung. */
export const sperrFehlerText = (e: RpcFehler | null | undefined): string => {
    if (fehltFunktion(e)) return MIGRATION_FEHLT;
    const m = e?.message || '';
    if (/eigene Konto ist gesperrt/.test(m)) return 'Ihr eigenes Konto ist gesperrt — Sie können niemanden sperren oder entsperren.';
    if (/eigene Konto lässt sich nicht/.test(m)) return 'Das eigene Konto lässt sich nicht selbst sperren oder entsperren.';
    if (/Super-Admin/.test(m)) return 'Einen Super-Admin kann nur ein Super-Admin sperren.';
    if (/nur Administratoren/.test(m)) return 'Nur Administratoren dürfen sperren und entsperren.';
    if (/Konto nicht gefunden/.test(m)) return 'Zu diesem Profil gibt es kein Anmeldekonto.';
    return m || 'Unbekannter Fehler.';
};

export async function ladeSperrstatus(): Promise<SperrStatusErgebnis> {
    const { data, error } = await supabase.rpc('benutzer_sperrstatus');
    if (error) return { verfuegbar: false, status: new Map(), grund: sperrFehlerText(error) };
    const status = new Map<string, SperrStatus>();
    for (const z of (data ?? []) as Array<{ user_id: string; gesperrt_bis: string | null; letzte_anmeldung: string | null }>) {
        status.set(z.user_id, { gesperrtBis: z.gesperrt_bis, letzteAnmeldung: z.letzte_anmeldung });
    }
    return { verfuegbar: true, status, grund: null };
}

export async function setzeSperre(
    userId: string,
    sperren: boolean,
): Promise<SperrErgebnis> {
    const { data, error } = await supabase.rpc('benutzer_sperren', { p_user: userId, p_sperren: sperren });
    if (error) return { ok: false, gesperrtBis: null, meldung: sperrFehlerText(error) };
    return { ok: true, gesperrtBis: (data as string | null) ?? null, meldung: null };
}
